/**
 * ReviewResponse Component
 * 
 * This component handles responding to GMB reviews, including:
 * - Display of review content and rating
 * - Response text editor for manual responses
 * - Character count validation (Google's 4,096 character limit)
 * - Save draft and publish functionality
 * - Response status tracking
 */

import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
// import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Star, 
  Send, 
  Clock, 
  MessageCircle, 
  Save,
  AlertCircle,
  Check
} from 'lucide-react'
import { publishReviewReply } from '@/utils/gmbPublish'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/hooks/use-toast'
import type { Review } from '@/types/reviews'

/**
 * Props interface for ReviewResponse component
 */
export interface ReviewResponseProps {
  /** The review to respond to */
  review: Review
  /** Callback when dialog should close */
  onClose?: () => void
  /** Callback after successful save/publish */
  onSuccess?: () => void
}

/**
 * Component for managing review responses
 */
export function ReviewResponse({
  review,
  onClose,
  onSuccess
}: ReviewResponseProps) {
  const [responseText, setResponseText] = useState('')
  const [responseStatus, setResponseStatus] = useState<'draft' | 'pending_approval' | 'published'>('draft')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const { toast } = useToast()
  
  // Character limit for Google reviews
  const CHARACTER_LIMIT = 4096
  const charactersRemaining = CHARACTER_LIMIT - responseText.length
  const isOverLimit = charactersRemaining < 0
  
  // Load existing response data
  useEffect(() => {
    if (review.response_text) {
      setResponseText(review.response_text)
      setResponseStatus(review.response_status || 'draft')
    } else if (review.reply_comment) {
      // Handle legacy GMB reply field
      setResponseText(review.reply_comment)
      setResponseStatus('published')
    }
  }, [review])

  // Calculate star display
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        }`}
      />
    ))
  }

  // Save response as draft
  const handleSaveDraft = async () => {
    if (!responseText.trim()) {
      setError('Please enter a response')
      return
    }

    if (isOverLimit) {
      setError(`Response exceeds the ${CHARACTER_LIMIT} character limit`)
      return
    }

    setIsSavingDraft(true)
    setError(null)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const { error: updateError } = await supabase
        .from('reviews')
        .update({
          response_text: responseText.trim(),
          response_status: 'draft',
          responded_by: user?.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', review.id)

      if (updateError) throw updateError

      toast({
        title: 'Draft saved',
        message: 'Your response has been saved as a draft.',
      })

      onSuccess?.()
    } catch (err) {
      console.error('Error saving draft:', err)
      setError('Failed to save draft. Please try again.')
    } finally {
      setIsSavingDraft(false)
    }
  }

  // Publish response to GMB
  const handlePublish = async () => {
    // Debug: Log the review data to verify gmb_review_id is present
    console.log('[ReviewResponse] Review data for publish:', {
      id: review.id,
      gmb_review_id: review.gmb_review_id,
      gmb_location_id: review.gmb_location_id,
      reviewer_name: review.reviewer_name,
      source: review.source
    })

    if (!responseText.trim()) {
      setError('Please enter a response')
      return
    }

    if (isOverLimit) {
      setError(`Response exceeds the ${CHARACTER_LIMIT} character limit`)
      return
    }

    // Only GMB reviews can be published
    if (!review.gmb_review_id || !review.gmb_location_id) {
      console.error('[ReviewResponse] Missing GMB IDs:', {
        gmb_review_id: review.gmb_review_id,
        gmb_location_id: review.gmb_location_id
      })
      setError('Only Google My Business reviews can be published')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // Publish to GMB - publishReviewReply handles database update
      // (updates reply_comment, reply_update_time, needs_response in reviews table)
      const result = await publishReviewReply({
        reviewId: review.gmb_review_id,
        replyText: responseText.trim(),
        locationId: review.gmb_location_id,
        databaseId: review.id  // Pass database UUID for fallback update
      })

      if (result.success) {
        setResponseStatus('published')
        toast({
          title: 'Response published',
          message: 'Your response has been published to Google My Business.',
        })
        onSuccess?.()
        onClose?.()
      } else {
        throw new Error(result.error || 'Failed to publish response')
      }
    } catch (err) {
      console.error('Error publishing response:', err)
      setError(err instanceof Error ? err.message : 'Failed to publish response. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Respond to Review</CardTitle>
          <div className="flex items-center gap-2">
            {responseStatus === 'published' && (
              <Badge variant="secondary" className="flex items-center gap-1 bg-green-100 text-green-800">
                <Check className="w-3 h-3" />
                Published
              </Badge>
            )}
            {responseStatus === 'draft' && review.response_text && (
              <Badge variant="secondary">Draft Saved</Badge>
            )}
            {!review.response_text && !review.reply_comment && (
              <Badge variant="destructive">Needs Response</Badge>
            )}
          </div>
        </div>
        <CardDescription>
          Craft a professional response to maintain your business reputation
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Review Display */}
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-primary">
                {review.reviewer_name.charAt(0).toUpperCase()}
              </span>
            </div>
            
            <div className="flex-1 space-y-1">
              <div className="flex items-center justify-between">
                <p className="font-medium">
                  {review.reviewer_name}
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex">{renderStars(review.star_rating_number || 0)}</div>
                  <span className="text-sm text-muted-foreground">
                    {new Date(review.reviewer_create_time).toLocaleDateString()}
                  </span>
                </div>
              </div>
              
              {review.reviewer_comment && (
                <p className="text-sm text-muted-foreground">{review.reviewer_comment}</p>
              )}
              
              <div className="flex items-center gap-2 mt-2">
                {review.location_name && (
                  <Badge variant="outline" className="text-xs">
                    {review.location_name}
                  </Badge>
                )}
                {review.source && (
                  <Badge variant="outline" className="text-xs">
                    {review.source.toUpperCase()}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Existing Reply */}
          {(review.reply_comment || (review.response_text && responseStatus === 'published')) && (
            <div className="mt-3 pl-13 border-l-2 border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <MessageCircle className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Published Response</span>
                {(review.reply_update_time || review.response_published_at) && (
                  <span className="text-xs text-muted-foreground">
                    • {new Date(review.reply_update_time || review.response_published_at!).toLocaleDateString()}
                  </span>
                )}
              </div>
              <p className="text-sm">{review.reply_comment || review.response_text}</p>
            </div>
          )}
        </div>

        {/* Response Editor */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">
              {responseStatus === 'published' ? 'Update Response' : 'Your Response'}
            </label>
            {responseStatus === 'draft' && review.response_text && (
              <Badge variant="secondary" className="text-xs">
                Draft saved {review.responded_at && `on ${new Date(review.responded_at).toLocaleDateString()}`}
              </Badge>
            )}
          </div>

          <Textarea
            value={responseText}
            onChange={(e) => setResponseText(e.target.value)}
            placeholder="Write a thoughtful, professional response..."
            className={`min-h-[150px] ${isOverLimit ? 'border-destructive' : ''}`}
            disabled={isSubmitting || isSavingDraft}
          />

          {/* Character count */}
          <div className="flex items-center justify-between text-sm">
            <span className={`flex items-center gap-1 ${isOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
              {isOverLimit && <AlertCircle className="h-3 w-3" />}
              {responseText.length} / {CHARACTER_LIMIT} characters
            </span>
            {charactersRemaining > 0 && charactersRemaining <= 100 && (
              <span className="text-amber-600">
                {charactersRemaining} characters remaining
              </span>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              disabled={isSubmitting || isSavingDraft || !responseText.trim() || isOverLimit}
              onClick={handleSaveDraft}
            >
              {isSavingDraft ? (
                <>
                  <Clock className="h-4 w-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-1" />
                  Save Draft
                </>
              )}
            </Button>
            <Button 
              onClick={handlePublish}
              disabled={isSubmitting || isSavingDraft || !responseText.trim() || isOverLimit || !review.gmb_review_id}
            >
              {isSubmitting ? (
                <>
                  <Clock className="h-4 w-4 mr-1 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1" />
                  {responseStatus === 'published' ? 'Update Response' : 'Publish Response'}
                </>
              )}
            </Button>
          </div>
        </div>
        
        {/* Info message for non-GMB reviews */}
        {!review.gmb_review_id && (
          <Alert className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This review is not from Google My Business. Responses can be saved as drafts but cannot be published to Google.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

export default ReviewResponse